import { useState, useEffect } from "react";
import { Badge } from "@shared/ui/badge";
import { Edit, Trash2, Eye } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@shared/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  useDeleteReason,
  useGetReconnectionReasons,
} from "@features/cx/settings/hooks";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { useCurrency } from "@shared/selectors/globalSelectors";

interface ReconnectionReason {
  id: number;
  code: string;
  configurationCode: string;
  name: string;
  longDescription: string;
  extraData: {
    responseTime: number;
    priorityLevel: number;
    resolutionTime: number;
    requiredApproval: boolean;
    acknowledgementTime: number;
    priorityLevelDisplay: string;
    fees?: Array<{ feeType: string; amount: number }>;
  };
}

interface ReconnectionReasonsTableProps {
  onEditReason: (reason: ReconnectionReason) => void;
}

export function ReconnectionReasonsTable({
  onEditReason,
}: ReconnectionReasonsTableProps) {
  const deleteReconnectionReason = useDeleteReason();
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [reasonToDelete, setReasonToDelete] =
    useState<ReconnectionReason | null>(null);
  const [reasonToDetail, setReasonToDetail] =
    useState<ReconnectionReason | null>(null);
  const [itemsPerPage] = useState(PAGE_SIZE);
  const { data: reconnectionReasonsData } = useGetReconnectionReasons(
    currentPage,
    PAGE_SIZE,
  );
  const currency = useCurrency();

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // Update pagination state when data changes
  useEffect(() => {
    const totalItems = reconnectionReasonsData?.count || 0;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    setPagination({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    });
  }, [reconnectionReasonsData, currentPage, itemsPerPage]);

  const handlePageChange = async (page: number) => {
    if (page !== currentPage && page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  };

  const handleDeleteReason = (reason: ReconnectionReason) => {
    setReasonToDelete(reason);
    setShowDeleteDialog(true);
  };

  const onDetailReason = (reason: ReconnectionReason) => {
    setReasonToDetail(reason);
    setShowDetailDialog(true);
  };

  const confirmDelete = () => {
    if (reasonToDelete) {
      const payloadDelete = {
        id: reasonToDelete.id,
      };
      deleteReconnectionReason.mutateAsync(payloadDelete);
      setShowDeleteDialog(false);
      setReasonToDelete(null);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "Critical":
        return "destructive";
      case "High":
        return "default";
      case "Medium":
        return "secondary";
      case "Low":
        return "outline";
      default:
        return "outline";
    }
  };

  const columns: TableColumn<ReconnectionReason>[] = [
    {
      key: "configurationCode",
      header: "Code",
      sortable: true,
      width: "w-24",
      render: (value: string) => <span className="font-mono">{value}</span>,
    },
    {
      key: "name",
      header: "Reason Name",
      sortable: true,
      render: (value: string) => <span className="font-medium">{value}</span>,
    },
    {
      key: "longDescription",
      header: "Description",
      sortable: false,
      width: "w-64",
      render: (value: string) => (
        <span className="max-w-xs truncate block" title={value}>
          {value}
        </span>
      ),
    },
    {
      key: "fees",
      header: <span className="flex justify-start w-full">Fees</span>,
      sortable: false,
      render: (value: any, row: ReconnectionReason) => {
        const fees = row.extraData.fees;
        if (!fees || fees.length === 0) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }
        const visibleFees = fees.slice(0, 3);
        const hiddenFees = fees.slice(3);
        return (
          <div className="flex flex-col items-center gap-1.5 w-full">
            {visibleFees.map((fee, idx) => (
              <div
                key={idx}
                className="inline-flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs w-full max-w-[180px]"
              >
                <span className="capitalize text-slate-500 font-medium">
                  {fee.feeType}
                </span>
                <span className="h-3 w-px bg-slate-300" />
                <span className="font-semibold text-slate-800 whitespace-nowrap">
                  {currency}
                  {fee.amount}
                </span>
              </div>
            ))}
            {hiddenFees.length > 0 && (
              <div className="relative group flex justify-center">
                <span className="text-xs text-blue-500 font-medium cursor-pointer hover:text-blue-700 hover:underline">
                  +{hiddenFees.length} more
                </span>
                <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 z-50 hidden group-hover:flex flex-col gap-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2 min-w-[180px]">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider pb-1 border-b border-slate-100 mb-0.5">
                    More Fees
                  </p>
                  {hiddenFees.map((fee, idx) => (
                    <div
                      key={idx}
                      className="inline-flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs"
                    >
                      <span className="capitalize text-slate-500 font-medium">
                        {fee.feeType}
                      </span>
                      <span className="h-3 w-px bg-slate-300" />
                      <span className="font-semibold text-slate-800 whitespace-nowrap">
                        {currency}
                        {fee.amount}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "acknowledgementTime",
      header: "Ack. Time (Hrs)",
      sortable: true,
      width: "w-32",
      render: (value: string, row: ReconnectionReason) => (
        <span className="font-medium">{row.extraData.acknowledgementTime}</span>
      ),
    },
    {
      key: "responseTime",
      header: "Response Time (Hrs)",
      sortable: true,
      width: "w-36",
      render: (value: string, row: ReconnectionReason) => (
        <span className="font-medium">{row.extraData.responseTime}</span>
      ),
    },
    {
      key: "resolutionTime",
      header: "Resolution Time (Hrs)",
      sortable: true,
      width: "w-40",
      render: (value: string, row: ReconnectionReason) => (
        <span className="font-medium">{row.extraData.resolutionTime}</span>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      sortable: true,
      width: "w-28",
      render: (value: string, row: ReconnectionReason) => (
        <Badge variant={getPriorityColor(row.extraData.priorityLevelDisplay)}>
          {row.extraData.priorityLevelDisplay}
        </Badge>
      ),
    },
    {
      key: "requiredApproval",
      header: "Requires Approval",
      sortable: true,
      width: "w-36",
      render: (value: boolean, row: ReconnectionReason) => (
        <Badge
          variant={row.extraData.requiredApproval ? "default" : "secondary"}
        >
          {row.extraData.requiredApproval ? "Yes" : "No"}
        </Badge>
      ),
    },
  ];

  const actions = [
    {
      label: "Edit",
      onClick: (row: ReconnectionReason) => {
        onEditReason(row);
      },
      icon: <Edit className="h-4 w-4 mr-2" />,
    },
    {
      label: "View",
      onClick: (row: ReconnectionReason) => {
        onDetailReason(row);
      },
      icon: <Eye className="h-4 w-4 mr-2" />,
    },
    {
      label: "Delete",
      onClick: (row: ReconnectionReason) => {
        handleDeleteReason(row);
      },
      icon: <Trash2 className="h-4 w-4 mr-2" />,
    },
  ];

  const detailFields = reasonToDetail
    ? [
        {
          label: "Code",
          value: (
            <span className="font-mono">
              {reasonToDetail.configurationCode}
            </span>
          ),
        },
        { label: "Name", value: reasonToDetail.name },
        {
          label: "Description",
          value: (
            <span className="whitespace-pre-wrap">
              {reasonToDetail.longDescription}
            </span>
          ),
          fullWidth: true,
        },
        {
          label: "Priority",
          value: (
            <Badge
              variant={getPriorityColor(
                reasonToDetail.extraData.priorityLevelDisplay,
              )}
            >
              {reasonToDetail.extraData.priorityLevelDisplay}
            </Badge>
          ),
        },
        {
          label: "Requires Approval",
          value: (
            <Badge
              variant={
                reasonToDetail.extraData.requiredApproval
                  ? "default"
                  : "secondary"
              }
            >
              {reasonToDetail.extraData.requiredApproval ? "Yes" : "No"}
            </Badge>
          ),
        },
        {
          label: "Ack. Time",
          value: `${reasonToDetail.extraData.acknowledgementTime} Hrs`,
        },
        {
          label: "Response Time",
          value: `${reasonToDetail.extraData.responseTime} Hrs`,
        },
        {
          label: "Resolution Time",
          value: `${reasonToDetail.extraData.resolutionTime} Hrs`,
        },
      ]
    : [];

  return (
    <div className="space-y-4 mt-5">
      <GenericTable
        data={reconnectionReasonsData?.results}
        columns={columns}
        rowKey="id"
        selectable={false}
        actions={actions}
        actionsType="dropdown"
        emptyMessage="No reconnection reasons found"
        loading={false}
        pagination={pagination}
        onPageChange={handlePageChange}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Reconnection Reason</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{reasonToDelete?.name}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reconnection Reason Details</DialogTitle>
          </DialogHeader>
          {reasonToDetail && (
            <div className="grid grid-cols-2 gap-6 py-4">
              {detailFields.map((field: any, idx) => (
                <div
                  key={idx}
                  className={field.fullWidth ? "col-span-2" : "col-span-1"}
                >
                  <ValueLabel label={field.label} value={field.value} />
                </div>
              ))}

              {reasonToDetail.extraData.fees &&
                reasonToDetail.extraData.fees.length > 0 && (
                  <div className="col-span-2 mt-4 space-y-3">
                    <p className="text-sm font-semibold border-b pb-2">
                      Reconnection Fees
                    </p>
                    <div className="space-y-3">
                      {reasonToDetail.extraData.fees.map((fee, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-100"
                        >
                          <div className="space-y-0.5">
                            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                              Fee Type
                            </p>
                            <p className="text-sm font-semibold">
                              {fee.feeType}
                            </p>
                          </div>
                          <div className="text-right space-y-0.5">
                            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                              Amount
                            </p>
                            <p className="text-sm font-bold text-blue-600">
                              {currency}
                              {fee.amount}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border border-blue-100 mt-2">
                        <p className="text-sm font-bold text-blue-900">
                          Total Fee Amount
                        </p>
                        <p className="text-sm font-black text-blue-900">
                          {currency}
                          {reasonToDetail.extraData.fees
                            .reduce((sum, fee) => sum + fee.amount, 0)
                            .toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
